import { useEffect, useMemo } from 'react';
import { SearchResultsSections } from '../SearchResults.types';
import { useVipAdsQuery } from './useVipAdsQuery';
import { useLatestAdsQuery } from './useLatestAdsQuery';
import { buildViewAllResultsHref } from './useSearchResultsData.helpers';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { AdOrder, ServiceName } from '@/api/types/ads.types';
import { useParseQuery } from '@/hooks/useParseQuery/useParseQuery';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { usePreviousValue } from '@/hooks/usePreviousValue';
import { usePopState } from '@/hooks/usePopState';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { useServerDataContext } from '@/contexts';
import { chunkArray } from '@/utils/helpers';
import { QUERY } from '@/constants/common';
import { useQueryParams } from '@/hooks/useQueryParams';

const KEYWORDS_ADS_REQUIRED_COUNT = 8;

interface UseSearchResultsDataOptions {
  sections: SearchResultsSections;
  onPaginationChange?: (hasNextPage: boolean) => void;
}

export function useSearchResultsData({
  sections,
  onPaginationChange,
}: UseSearchResultsDataOptions) {
  const { currentCategory: serverCurrentCategory } = useServerDataContext();
  const { isAllCategories, currentCategory = serverCurrentCategory } =
    useCurrentCategory();
  const { isPopState, resetPopState } = usePopState();
  const {
    keywords,
    price,
    regionId,
    order,
    propertyOptions,
    userLegacyId,
    isShop,
  } = useParseQuery();
  const source = usePlatformSource();
  const [keywordsSourceParam] = useQueryParams(QUERY.keywordsSource);
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  const showVip = sections.vipAds !== false;
  const showLatest = sections.latestAds !== false;
  const vipChunkSize =
    showVip && showLatest ? AdsChunkSize.Small : AdsChunkSize.Middle;
  const latestChunkSize = AdsChunkSize.Middle;
  const vipOrderType =
    showVip && showLatest ? AdOrder.Random : AdOrder.VippedAtDesc;

  const categoryId = currentCategory?.id;
  const filters = useMemo(
    () => ({
      categoryId: isAllCategories ? null : categoryId,
      price,
      regionId,
      propertyOptions,
      userLegacyId,
      isShop,
    }),
    [
      isAllCategories,
      categoryId,
      price,
      regionId,
      propertyOptions,
      userLegacyId,
      isShop,
    ]
  );

  const vip = useVipAdsQuery({
    variables: {
      orderType: vipOrderType,
      adKind: ServiceName.Vip,
      first: vipChunkSize,
      keywords,
      filters,
      source,
    },
    chunkSize: vipChunkSize,
    skip: !showVip,
  });

  const latest = useLatestAdsQuery({
    variables: {
      orderType: order,
      first: latestChunkSize,
      keywords,
      filters,
      keywordsSource: keywordsSourceParam?.toUpperCase(),
      sourceLink: currentUrl,
      source,
    },
    chunkSize: latestChunkSize,
    skip: !showLatest,
  });

  const resetKey = useMemo(
    () => JSON.stringify({ ...filters, keywords, order }),
    [filters, keywords, order]
  );
  const prevResetKey = usePreviousValue(resetKey);

  useEffect(() => {
    if (prevResetKey && prevResetKey !== resetKey && !isPopState) {
      if (showVip) vip.refetchAndNormalize();
      if (showLatest) latest.refetchAndNormalize();
    }
    resetPopState();
  }, [resetKey]);

  const isLoading =
    (showVip && vip.isLoading) || (showLatest && latest.isLoading);
  const primaryHasNextPage = showLatest ? latest.hasNextPage : vip.hasNextPage;
  const hasPrimaryData = showLatest
    ? latest.nodes.length > 0
    : vip.nodes.length > 0;

  useEffect(() => {
    if (onPaginationChange) {
      onPaginationChange(hasPrimaryData ? primaryHasNextPage : true);
    }
  }, [hasPrimaryData, primaryHasNextPage, onPaginationChange]);

  const hasEnoughKeywordsAdsForCta =
    latest.keywordsAds.length >= KEYWORDS_ADS_REQUIRED_COUNT;
  const shouldShowViewAllButton =
    hasEnoughKeywordsAdsForCta && !isAllCategories && Boolean(keywords);
  const viewAllResultsHref = useMemo(
    () =>
      shouldShowViewAllButton && keywords
        ? buildViewAllResultsHref(keywords)
        : null,
    [shouldShowViewAllButton, keywords]
  );

  const { chunkedVipAds, chunkedLatestAds } = useMemo(
    () => ({
      chunkedVipAds: chunkArray(vip.nodes, vipChunkSize),
      chunkedLatestAds: chunkArray(latest.nodes, latestChunkSize),
    }),
    [vip.nodes, latest.nodes, vipChunkSize, latestChunkSize]
  );

  const fetchMoreVipAds = () =>
    vip.fetchMoreAds(!showLatest ? vip.endCursor : undefined);
  const fetchMoreLatestAds = () => latest.fetchMoreAds(latest.endCursor);

  return {
    chunkedVipAds,
    chunkedLatestAds,
    isLoading,
    hasNextPage: primaryHasNextPage,
    fetchMoreVipAds: showVip ? fetchMoreVipAds : undefined,
    fetchMoreLatestAds: showLatest ? fetchMoreLatestAds : undefined,
    vipChunkSize,
    showVip,
    showLatest,
    keywordsAds: latest.keywordsAds,
    viewAllResultsHref,
  };
}
